import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../elements/Modal';
import { getBrowserLocale } from '../../../utils/language';

function toDateInputValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateForDisplay(date, locale = 'en-GB') {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function parseDateValue(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const [year, month, day] = String(value)
      .split('-')
      .map((part) => parseInt(part, 10));
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function ResumeSubscriptionModal({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  selectedLang,
  subscriptionAmount,
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('IMMEDIATE');
  const [resumeDate, setResumeDate] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const locale = getBrowserLocale(selectedLang);
  const todayInput = useMemo(() => toDateInputValue(new Date()), []);

  useEffect(() => {
    if (open) {
      setMode('IMMEDIATE');
      setResumeDate('');
      setShowConfirmation(false);
    }
  }, [open]);

  const parsedSelectedDate = parseDateValue(resumeDate);
  const isDateMode = mode === 'ON_DATE';
  const isDateValid = !isDateMode || (resumeDate && resumeDate >= todayInput);
  const formattedAmount = Number(subscriptionAmount || 0).toFixed(2);

  const handleProceed = () => {
    if (!isDateValid) return;
    setShowConfirmation(true);
  };

  return (
    <Modal open={open} handleClose={onClose} className="!t-max-w-[95vw] lg:!t-max-w-[400px]">
      <div className="t-p-6 t-flex t-flex-col t-gap-4">
        {!showConfirmation ? (
          <>
            <h2 className="t-text-xl t-font-bold t-m-0">{t('ResumeModal_Title')}</h2>
            <p className="t-text-sm t-text-gray-600 t-m-0">{t('ResumeModal_Subtitle')}</p>

            <div className="t-flex t-flex-col t-gap-3">
              <label className="t-flex t-items-start t-gap-3 t-rounded-lg t-border t-border-gray-200 t-px-3 t-py-2 t-cursor-pointer">
                <input
                  type="radio"
                  name="resume-mode"
                  className="t-mt-1"
                  value="IMMEDIATE"
                  checked={mode === 'IMMEDIATE'}
                  onChange={() => setMode('IMMEDIATE')}
                />
                <div className="t-flex t-flex-col">
                  <span className="t-text-sm t-font-medium t-text-gray-900">
                    {t('ResumeModal_Option_Immediate')}
                  </span>
                  <span className="t-text-xs t-text-gray-600">{t('ResumeModal_Option_Immediate_Desc')}</span>
                </div>
              </label>

              <label className="t-flex t-items-start t-gap-3 t-rounded-lg t-border t-border-gray-200 t-px-3 t-py-2 t-cursor-pointer">
                <input
                  type="radio"
                  name="resume-mode"
                  className="t-mt-1"
                  value="ON_DATE"
                  checked={mode === 'ON_DATE'}
                  onChange={() => setMode('ON_DATE')}
                />
                <div className="t-flex t-flex-col t-flex-1">
                  <span className="t-text-sm t-font-medium t-text-gray-900">{t('ResumeModal_Option_Date')}</span>
                  <span className="t-text-xs t-text-gray-600">{t('ResumeModal_Option_Date_Desc')}</span>
                  {mode === 'ON_DATE' && (
                    <input
                      type="date"
                      value={resumeDate}
                      min={todayInput}
                      className="t-mt-2 t-border t-border-gray-300 t-rounded-md t-px-2 t-py-1 t-text-sm"
                      onChange={(e) => setResumeDate(e.target.value)}
                    />
                  )}
                </div>
              </label>
            </div>

            {mode === 'ON_DATE' && resumeDate && (
              <p className="t-text-xs t-text-gray-700 t-m-0 t-rounded-md t-bg-gray-50 t-px-3 t-py-2">
                {t('ResumeModal_AfterCycle_Note', {
                  date: formatDateForDisplay(parsedSelectedDate, locale),
                })}
              </p>
            )}

            <div className="t-flex t-flex-col sm:t-flex-row t-items-stretch sm:t-items-center t-gap-3 t-mt-2">
              <button
                type="button"
                className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-secondary t-text-white hover:t-bg-teal-800 disabled:t-opacity-60"
                onClick={handleProceed}
                disabled={!isDateValid || isSubmitting}
              >
                {t('ResumeModal_Confirm')}
              </button>
              <button
                type="button"
                className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-red-400 t-text-white hover:t-bg-red-500 disabled:t-opacity-60"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('ResumeModal_Cancel')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="t-text-xl t-font-bold t-m-0">{t('ResumeModal_Confirmation_Title')}</h2>
            <p className="t-text-sm t-text-gray-700 t-m-0">
              {mode === 'IMMEDIATE'
                ? t('ResumeModal_Confirmation_Immediate', { amount: formattedAmount })
                : t('ResumeModal_Confirmation_OnDate', {
                    amount: formattedAmount,
                    date: formatDateForDisplay(parsedSelectedDate, locale),
                  })}
            </p>
            <div className="t-flex t-flex-col sm:t-flex-row t-items-stretch sm:t-items-center t-gap-3 t-mt-2">
              <button
                type="button"
                className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-secondary t-text-white hover:t-bg-teal-800 disabled:t-opacity-60"
                onClick={() => onConfirm({ mode, resumeDate: mode === 'ON_DATE' ? resumeDate : null })}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('ResumeModal_Confirm_Loading') : t('ResumeModal_Confirm_Final')}
              </button>
              <button
                type="button"
                className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-red-400 t-text-white hover:t-bg-red-500 disabled:t-opacity-60"
                onClick={() => setShowConfirmation(false)}
                disabled={isSubmitting}
              >
                {t('ResumeModal_Back')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default ResumeSubscriptionModal;
